import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { api, type CurrentUser } from '@/api/client';
import {
  Navigator,
  SplitView,
  StatusBar,
  removeStored,
  storedKeys,
  type NavNode,
} from '@/components/desktop';
import { Modal, SearchBar } from '@/components/ui';
import { AboutFacts } from '@/shell/About';
import { SHORTCUTS } from '@/shell/shortcuts';
import { useAuth } from '@/stores/auth';
import { formatTime } from '@/format';
import { toast } from '@/stores/toast';
import { CompanyPage } from './settings/CompanyPage';
import { DashboardPage } from './settings/DashboardPage';
import { ResetData } from './settings/ResetData';
import {
  SECTIONS,
  rememberSettingsPage,
  storedSection,
  useSettingsSection,
  visibleSections,
  type SectionDef,
  type SectionId,
} from './settings/pages';
import { AccessLines } from './iam/AccessLines';
import { Sessions } from './settings/Sessions';
import { TwoStep } from './settings/TwoStep';
import {
  DEFAULT_PREFERENCES,
  changedKeys,
  usePreferences,
  type PreferenceKey,
  type Preferences,
} from '@/stores/preferences';

type Control =
  | {
      kind: 'slider';
      min: number;
      max: number;
      step: number;
      format: (v: number) => string;
    }
  | { kind: 'choice'; options: Array<{ value: string; label: string }> }
  | { kind: 'toggle' };

interface SettingDef {
  key: PreferenceKey;
  section: SectionId;
  label: string;
  hint: (prefs: Preferences) => string;
  keywords?: string;
  control: Control;
}

/**
 * Every preference, described once. The pages, the search and the "changed"
 * count all read from this list, so a new preference is one entry here.
 */
const SETTINGS: SettingDef[] = [
  {
    key: 'uiScale',
    section: 'display',
    label: 'Screen size',
    hint: () => 'Makes everything bigger or smaller, including the space between things.',
    keywords: 'interface scale zoom',
    control: {
      kind: 'slider',
      min: 0.8,
      max: 1.5,
      step: 0.05,
      format: (v) => `${Math.round(v * 100)}%`,
    },
  },
  {
    key: 'fontSize',
    section: 'display',
    label: 'Text size',
    hint: () => 'Changes only the text. Use it for bigger text without losing space on the screen.',
    keywords: 'font letters read',
    control: {
      kind: 'slider',
      min: 12,
      max: 20,
      step: 1,
      format: (v) => `${v}px`,
    },
  },
  {
    key: 'language',
    section: 'display',
    label: 'Language',
    hint: () => 'Urdu shows the screens from right to left. Most labels are still in English for now.',
    keywords: 'urdu english rtl',
    control: {
      kind: 'choice',
      options: [
        { value: 'en', label: 'English' },
        { value: 'ur', label: 'اردو' },
      ],
    },
  },
  {
    key: 'targetSize',
    section: 'accessibility',
    label: 'Button size',
    hint: () =>
      'The smallest size of buttons, rows and boxes. Choose Large on a tablet in a shed, or when you wear gloves.',
    keywords: 'touch target tablet gloves',
    control: {
      kind: 'choice',
      options: [
        { value: 'compact', label: 'Compact' },
        { value: 'normal', label: 'Normal' },
        { value: 'large', label: 'Large' },
        { value: 'extra-large', label: 'Extra large' },
      ],
    },
  },
  {
    key: 'rowDensity',
    section: 'accessibility',
    label: 'Row spacing',
    hint: () => 'The space around each row in a list.',
    keywords: 'table density compact relaxed',
    control: {
      kind: 'choice',
      options: [
        { value: 'compact', label: 'Compact' },
        { value: 'normal', label: 'Normal' },
        { value: 'relaxed', label: 'Relaxed' },
      ],
    },
  },
  {
    key: 'highContrast',
    section: 'accessibility',
    label: 'High contrast',
    hint: () => 'Black text on white, with darker borders.',
    keywords: 'colour color dark eyesight',
    control: { kind: 'toggle' },
  },
  {
    key: 'underlineLinks',
    section: 'accessibility',
    label: 'Underline links',
    hint: () => 'Draws a line under links, so you do not need colour to see them.',
    control: { kind: 'toggle' },
  },
  {
    key: 'reducedMotion',
    section: 'accessibility',
    label: 'Less movement',
    hint: () => 'Stops moving text and animations.',
    keywords: 'reduce motion animation ticker',
    control: { kind: 'toggle' },
  },
  {
    key: 'windowMode',
    section: 'windows',
    label: 'Opening a module',
    hint: (p) =>
      p.windowMode === 'new'
        ? 'Each module opens in its own window, so you can put one on each monitor. Clicking the same module again brings its window to the front.'
        : 'A module opens in this window, in place of what you are looking at.',
    keywords: 'new window monitor multiple',
    control: {
      kind: 'choice',
      options: [
        { value: 'same', label: 'In this window' },
        { value: 'new', label: 'In a new window' },
      ],
    },
  },
];

/**
 * Settings, laid out like a desktop application's options window: the list
 * of pages on the left, the chosen page on the right, a search that looks
 * across every page, and a status line saying whether the last change was
 * saved.
 *
 * Every control still takes effect the moment it changes — a setting whose
 * result you cannot see is one you cannot judge. Saving happens in the
 * background after a short pause and is reported, never blocking the change.
 */
export function Settings() {
  const { user, can } = useAuth();
  const prefs = usePreferences((s) => s.prefs);
  const chosen = useSettingsSection((s) => s.section);
  // Pages behind a permission are not shown, or even searched, without it.
  const sections = visibleSections(can);
  const wanted = chosen ?? storedSection();
  const section = sections.some((s) => s.id === wanted) ? wanted : 'display';
  const [query, setQuery] = useState('');

  // The next visit starts from the remembered page, not this visit's state.
  useEffect(() => () => useSettingsSection.setState({ section: null }), []);

  const needle = query.trim().toLowerCase();
  const matches = needle ? searchSettings(needle, prefs, sections) : null;

  if (!user) return null;

  function setSection(id: string) {
    rememberSettingsPage(id as SectionId);
    setQuery('');
  }

  const nodes: NavNode[] = sections.map((s) => ({
    id: s.id,
    label: s.label,
    // Only pages with results get a count; a row of zeros is noise.
    count: matches && matches.counts[s.id] > 0 ? matches.counts[s.id] : undefined,
  }));
  // While searching, the tree points at the first page with a result rather
  // than at the page open before the search.
  const treeSelection = (matches && sections.find((s) => matches.counts[s.id] > 0)?.id) || section;

  const current = sections.find((s) => s.id === section)!;

  return (
    <div className="workbench settingsapp">
      <SplitView
        id="settings"
        initial={230}
        firstLabel="settings list"
        first={
          <div className="settingsapp__nav">
            <SearchBar value={query} onChange={setQuery} placeholder="Search settings" />
            <Navigator
              id="settings"
              label="Settings pages"
              nodes={nodes}
              selectedId={treeSelection}
              onSelect={setSection}
            />
          </div>
        }
        second={
          <div className="workbench__main workbench__main--form settingsapp__page">
            {matches ? (
              <SearchResults
                matches={matches}
                query={query.trim()}
                sections={sections}
                onOpen={setSection}
              />
            ) : (
              <SectionPage id={section} user={user} />
            )}
          </div>
        }
      />
      <SettingsStatus
        place={matches ? `${matches.total} found` : current.label}
        personal={Boolean(matches) || PERSONAL_PAGES.includes(section)}
      />
    </div>
  );
}

function SectionPage({ id, user }: { id: SectionId; user: CurrentUser }) {
  switch (id) {
    case 'display':
      return (
        <PreferencePage
          section="display"
          title="Display"
          intro="These settings are saved to your account. They follow you to any computer you sign in on."
        >
          <Preview />
        </PreferencePage>
      );
    case 'accessibility':
      return (
        <PreferencePage
          section="accessibility"
          title="Accessibility"
          intro="Make the screens easier to see and to use."
        />
      );
    case 'windows':
      return (
        <PreferencePage
          section="windows"
          title="Windows & layout"
          intro="How modules open, and the layout each screen remembers."
        >
          <LayoutReset />
        </PreferencePage>
      );
    case 'dashboard':
      return <DashboardPage />;
    case 'keyboard':
      return <KeyboardPage />;
    case 'password':
      return <PasswordPage />;
    case 'account':
      return <AccountPage user={user} />;
    case 'company':
      return <CompanyPage />;
    case 'reset':
      return <ResetData />;
    case 'about':
      return <AboutPage />;
  }
}

/** One page of preferences, with a reset for the whole page. */
function PreferencePage({
  section,
  title,
  intro,
  children,
}: {
  section: SectionId;
  title: string;
  intro: string;
  children?: ReactNode;
}) {
  const prefs = usePreferences((s) => s.prefs);
  const resetKeys = usePreferences((s) => s.resetKeys);
  const defs = SETTINGS.filter((s) => s.section === section);
  const changed = defs.filter((d) => prefs[d.key] !== DEFAULT_PREFERENCES[d.key]);

  return (
    <section className="settingsapp__section" aria-labelledby={`settings-${section}`}>
      <header className="settingsapp__head">
        <div>
          <h2 id={`settings-${section}`}>{title}</h2>
          <p className="muted settings__intro">{intro}</p>
        </div>
        <button
          type="button"
          className="btn btn--sm"
          disabled={changed.length === 0}
          title={changed.length === 0 ? 'Everything on this page is already at its default' : undefined}
          onClick={() => resetKeys(changed.map((d) => d.key))}
        >
          Reset this page
        </button>
      </header>

      <div className="setlist">
        {defs.map((d) => (
          <SettingRow key={d.key} def={d} />
        ))}
      </div>

      {children}
    </section>
  );
}

function SettingRow({ def }: { def: SettingDef }) {
  const prefs = usePreferences((s) => s.prefs);
  const set = usePreferences((s) => s.set);
  const resetKeys = usePreferences((s) => s.resetKeys);
  const value = prefs[def.key];
  const changed = value !== DEFAULT_PREFERENCES[def.key];
  const labelId = `setting-${def.key}`;
  const hintId = `setting-${def.key}-hint`;
  const update = (v: unknown) => set({ [def.key]: v } as Partial<Preferences>);

  let control: ReactNode;
  const c = def.control;
  if (c.kind === 'slider') {
    control = (
      <div className="setrow__slider">
        <input
          type="range"
          min={c.min}
          max={c.max}
          step={c.step}
          value={value as number}
          onChange={(e) => update(Number(e.target.value))}
          aria-labelledby={labelId}
          aria-describedby={hintId}
          aria-valuetext={c.format(value as number)}
          className="settings__slider"
        />
        <span className="setrow__value">{c.format(value as number)}</span>
      </div>
    );
  } else if (c.kind === 'choice') {
    control = (
      <div className="settings__choices" role="group" aria-labelledby={labelId} aria-describedby={hintId}>
        {c.options.map((o) => (
          <button
            key={o.value}
            type="button"
            className={`chip ${o.value === value ? 'chip--on' : ''}`}
            aria-pressed={o.value === value}
            onClick={() => update(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    );
  } else {
    control = (
      <label className="settings__toggle">
        <input
          type="checkbox"
          checked={value as boolean}
          onChange={(e) => update(e.target.checked)}
          aria-labelledby={labelId}
          aria-describedby={hintId}
        />
        <span>{value ? 'On' : 'Off'}</span>
      </label>
    );
  }

  return (
    <div className={`setrow${changed ? ' setrow--changed' : ''}`}>
      <div className="setrow__text">
        <span className="setrow__label" id={labelId}>
          {def.label}
          {changed && <span className="sr-only"> (changed)</span>}
        </span>
        <p className="settings__hint" id={hintId}>
          {def.hint(prefs)}
        </p>
      </div>
      <div className="setrow__control">{control}</div>
      <div className="setrow__reset">
        {changed && (
          <button
            type="button"
            className="linkbutton"
            onClick={() => resetKeys([def.key])}
            aria-label={`Reset ${def.label}`}
            title={`Back to the default: ${describeDefault(def)}`}
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
}

function describeDefault(def: SettingDef): string {
  const v = DEFAULT_PREFERENCES[def.key];
  const c = def.control;
  if (c.kind === 'slider') return c.format(v as number);
  if (c.kind === 'choice') return c.options.find((o) => o.value === v)?.label ?? String(v);
  return v ? 'On' : 'Off';
}

interface Matches {
  settings: SettingDef[];
  pages: SectionDef[];
  counts: Record<SectionId, number>;
  total: number;
}

/**
 * `sections` is the pages this person may see, so a page behind a permission
 * they lack cannot be reached through the search either — neither by its own
 * name nor by a setting that happens to live on it.
 */
function searchSettings(needle: string, prefs: Preferences, sections: SectionDef[]): Matches {
  const words = needle.split(/\s+/);
  const hit = (text: string) => words.every((w) => text.toLowerCase().includes(w));

  const settings = SETTINGS.filter((d) => {
    const section = sections.find((s) => s.id === d.section);
    if (!section) return false;
    return hit(`${d.label} ${d.hint(prefs)} ${d.keywords ?? ''} ${section.label}`);
  });
  // Pages without preference rows (password, account, keyboard) are found by
  // their own name and keywords.
  const pages = sections.filter(
    (s) => !settings.some((d) => d.section === s.id) && hit(`${s.label} ${s.keywords}`),
  );

  const counts = Object.fromEntries(SECTIONS.map((s) => [s.id, 0])) as Record<SectionId, number>;
  for (const d of settings) counts[d.section]++;
  for (const p of pages) counts[p.id]++;

  return { settings, pages, counts, total: settings.length + pages.length };
}

function SearchResults({
  matches,
  query,
  sections,
  onOpen,
}: {
  matches: Matches;
  query: string;
  /** The pages this person may see, in the order the list shows them. */
  sections: SectionDef[];
  onOpen: (id: SectionId) => void;
}) {
  if (matches.total === 0) {
    return (
      <section className="settingsapp__section">
        <h2>Search</h2>
        <p className="muted">No settings match “{query}”. Try a shorter word.</p>
      </section>
    );
  }

  return (
    <section className="settingsapp__section" aria-label="Search results">
      <h2>Search</h2>
      {sections.map((s) => {
        const rows = matches.settings.filter((d) => d.section === s.id);
        const page = matches.pages.some((p) => p.id === s.id);
        if (rows.length === 0 && !page) return null;
        return (
          <div key={s.id} className="settingsapp__group">
            <h3>
              <button type="button" className="linkbutton" onClick={() => onOpen(s.id)}>
                {s.label}
              </button>
            </h3>
            {rows.length > 0 && (
              <div className="setlist">
                {rows.map((d) => (
                  <SettingRow key={d.key} def={d} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}

/** Shows the settings acting on real controls, not a description of them. */
function Preview() {
  return (
    <div className="settingsapp__preview">
      <h3>Preview</h3>
      <p className="muted settings__hint">These are real controls at your current settings.</p>

      <div className="settings__preview">
        <button type="button" className="btn btn--primary">
          Post shipment
        </button>
        <button type="button" className="btn">
          Save draft
        </button>
        <input className="field__input" defaultValue="120.5" aria-label="Example quantity" />
      </div>

      <div className="tablewrap settings__previewTable">
        <table className="table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Item</th>
              <th className="num">Quantity</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="mono">F-51-01</td>
              <td>Broiler Starter Crumble</td>
              <td className="num">420</td>
              <td>
                <span className="pill">OK</span>
              </td>
            </tr>
            <tr>
              <td className="mono">M-11-04</td>
              <td>Enrofloxacin 10% Oral</td>
              <td className="num">8.80</td>
              <td>
                <span className="pill pill--alert">⚠ Below threshold</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * What each screen remembers on this computer: pane sizes, column widths,
 * open tree branches and the last place chosen. Nothing here is on the
 * account — it is about this screen and this monitor.
 */
const LAYOUT_PREFIXES = [
  'poultry.split.',
  'poultry.grid.',
  'poultry.nav.',
  'poultry.farmManager.',
  'poultry.reference.',
];

function layoutKeys(): string[] {
  return storedKeys(LAYOUT_PREFIXES);
}

function LayoutReset() {
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(false);
  const [count, setCount] = useState(() => layoutKeys().length);

  function reset() {
    for (const key of layoutKeys()) removeStored(key);
    setCount(0);
    setConfirming(false);
    setDone(true);
    toast.success('Layout reset. Each screen will open with its first layout.');
  }

  return (
    <div className="settingsapp__block">
      <h3>Saved layout</h3>
      <p className="settings__hint">
        Screens remember pane sizes, column widths and which folders are open. This is saved on this computer,
        for you only. It is not on your account.
      </p>
      <div className="settingsapp__blockrow">
        <button
          type="button"
          className="btn btn--sm"
          disabled={count === 0}
          title={count === 0 ? 'Every screen is already at its first layout' : undefined}
          onClick={() => setConfirming(true)}
        >
          Reset layout
        </button>
        {done && (
          <span className="settings__ok" role="status">
            <span aria-hidden="true">✓ </span>
            Layout reset. Each screen will open with its first layout.
          </span>
        )}
      </div>

      {confirming && (
        <Modal title="Reset layout" onClose={() => setConfirming(false)}>
          <div className="stack">
            <h2>Reset layout?</h2>
            <p>
              Every screen will forget its pane sizes, column widths and open folders on this computer. Your
              settings and your data do not change.
            </p>
            <div className="formactions">
              <button type="button" className="btn" onClick={() => setConfirming(false)}>
                Cancel
              </button>
              <span className="formactions__spacer" />
              <button type="button" className="btn btn--primary" onClick={reset}>
                Reset layout
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function KeyboardPage() {
  return (
    <section className="settingsapp__section" aria-labelledby="settings-keyboard">
      <header className="settingsapp__head">
        <div>
          <h2 id="settings-keyboard">Keyboard</h2>
          <p className="muted settings__intro">Keys that work on the screens. You cannot change them yet.</p>
        </div>
      </header>
      <div className="settingsapp__keys">
        {SHORTCUTS.map((g) => (
          <div key={g.group} className="tablewrap">
            <table className="table">
              <caption className="settingsapp__caption">{g.group}</caption>
              <tbody>
                {g.rows.map(([keys, does]) => (
                  <tr key={keys}>
                    <td className="settingsapp__key">
                      <kbd>{keys}</kbd>
                    </td>
                    <td>{does}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Changing your own password. `required` is the state after a complete wipe
 * put the account back on the default password: the rest of the app stays
 * shut until it is changed, so the other-devices list is not shown.
 */
export function PasswordPage({ required = false }: { required?: boolean } = {}) {
  const me = useAuth((s) => s.user);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    // Checked here as well as on the server so the user is told before a
    // round trip that would reject it anyway.
    if (next.length < 10) return setError('The new password must have at least 10 characters.');
    if (next.length > 128) return setError('The new password can have at most 128 characters.');
    if (next !== confirm) return setError('The two new passwords are not the same.');
    if (next === current) return setError('The new password must be different from the current one.');
    if (me && hasPersonalDetail(next, me)) {
      return setError('Do not use your user ID, your name or your mobile number in your password.');
    }

    setBusy(true);
    try {
      const res = await api.post<{ message: string }>('/api/v1/settings/password', {
        currentPassword: current,
        newPassword: next,
      });
      // The password now travels to every device, this one included: the
      // server has already ended every session over it. Signing out here
      // too, rather than waiting for the next request to come back 401, is
      // what keeps the screen honest — nothing on it still works. The
      // sign-in screen shows the server's own message, naming how many
      // devices that covered.
      useAuth.getState().endSession(res.message);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change the password.');
    } finally {
      setBusy(false);
    }
  }

  const type = show ? 'text' : 'password';
  const tempUntil = me?.tempPasswordExpiresAt ? new Date(me.tempPasswordExpiresAt) : null;
  const rules = [
    { ok: next.length >= 10, text: 'At least 10 characters' },
    { ok: next.length > 0 && !(me && hasPersonalDetail(next, me)), text: 'Not your user ID, name or mobile number' },
    { ok: next.length > 0 && next !== current, text: 'Different from your current password' },
    { ok: next.length > 0 && next === confirm, text: 'Both new passwords the same' },
  ];

  return (
    <section className="settingsapp__section" aria-labelledby="settings-password">
      <header className="settingsapp__head">
        <div>
          <h2 id="settings-password">Password & sign-in</h2>
          {required && (
            <p className="login__notice" role="status">
              <span aria-hidden="true">ⓘ </span>
              {tempUntil
                ? `Your administrator gave you a temporary password. Choose your own now. The temporary one stops working on ${tempUntil.toLocaleDateString()}.`
                : 'Choose a new password before you carry on. Your current password is the default one you signed in with.'}
            </p>
          )}
          <p className="muted settings__intro">
            Changing your password signs you out everywhere, including here — this device too. We ask for
            your current password so nobody can change it from a computer you left open; once it changes,
            sign in again with the new one.
          </p>
        </div>
      </header>

      <form onSubmit={submit} className="settings__form">
        <label className="field">
          <span className="field__label">Current password</span>
          <input
            className="field__input"
            type={type}
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>

        <div className="field">
          <label className="field__label" htmlFor="password-new">
            New password
          </label>
          <input
            id="password-new"
            className="field__input"
            type={type}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            aria-describedby="password-rule"
            required
          />
          <ul className="pwrules" id="password-rule" aria-live="polite">
            {rules.map((r) => (
              <li key={r.text} className={r.ok ? 'pwrules__ok' : undefined}>
                <span aria-hidden="true">{r.ok ? '✓' : '○'} </span>
                {r.text}
                <span className="sr-only">{r.ok ? ' — done' : ' — not yet'}</span>
              </li>
            ))}
          </ul>
          <span className="settings__hint">
            Common passwords such as pakistan123 are refused, and so are your last five.
          </span>
        </div>

        <label className="field">
          <span className="field__label">New password again</span>
          <input
            className="field__input"
            type={type}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
          />
        </label>

        <label className="settings__toggle">
          <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
          <span>Show passwords</span>
        </label>

        {error && (
          <p className="errornote" role="alert">
            <span aria-hidden="true">⚠ </span>
            {error}
          </p>
        )}

        <div>
          <button className="btn btn--primary" disabled={busy}>
            {busy ? 'Changing…' : 'Change password'}
          </button>
        </div>
      </form>

      {!required && <TwoStep />}
      {!required && <Sessions />}
    </section>
  );
}

/** The same rule the server applies: pieces of four letters or more. */
function hasPersonalDetail(
  password: string,
  me: { userId: string; fullName: string },
): boolean {
  const folded = password.toLowerCase();
  const pieces = [
    me.userId.toLowerCase(),
    ...me.userId.toLowerCase().split(/[._-]+/),
    ...me.fullName.toLowerCase().split(/\s+/),
  ];
  return pieces.some((p) => p.length >= 4 && folded.includes(p));
}

function AboutPage() {
  return (
    <section className="settingsapp__section" aria-labelledby="settings-about">
      <header className="settingsapp__head">
        <div>
          <h2 id="settings-about">About</h2>
          <p className="muted settings__intro">
            Which version of the app and the server you are using. Give these to your administrator when you
            report a problem.
          </p>
        </div>
      </header>
      <AboutFacts />
    </section>
  );
}

function AccountPage({ user }: { user: CurrentUser }) {
  const [showPermissions, setShowPermissions] = useState(false);
  // What I can do, in plain lines, with farm names — read from the server so
  // it is the same explanation an administrator sees in Users.
  const access = useQuery({
    queryKey: ['users', 'me', 'access'],
    queryFn: () => api.get('/api/v1/users/me/access'),
  });
  const roleNames = user.roleNames ?? user.roles;

  return (
    <section className="settingsapp__section" aria-labelledby="settings-account">
      <header className="settingsapp__head">
        <div>
          <h2 id="settings-account">My account</h2>
          <p className="muted settings__intro">
            Only an administrator can change your roles, farms or permissions.
          </p>
        </div>
      </header>

      <dl className="settings__facts">
        <div>
          <dt>Name</dt>
          <dd>{user.fullName}</dd>
        </div>
        <div>
          <dt>User ID</dt>
          <dd className="mono mono--ascase">{user.userId}</dd>
        </div>
        <div>
          <dt>{roleNames.length === 1 ? 'Role' : 'Roles'}</dt>
          <dd>{roleNames.join(', ') || 'None'}</dd>
        </div>
        <div>
          <dt>Farms</dt>
          <dd>
            {access.data?.farms ??
              (user.scope.allFarms ? 'All farms' : `${user.scope.farmIds.length} farm(s)`)}
          </dd>
        </div>
        <div>
          <dt>Two-step sign-in</dt>
          <dd>{user.twoStepEnabled ? 'On' : 'Off'}</dd>
        </div>
      </dl>

      <div className="settingsapp__block">
        <button
          type="button"
          className="btn btn--sm"
          aria-expanded={showPermissions}
          onClick={() => setShowPermissions((v) => !v)}
        >
          {showPermissions ? 'Hide what I can do' : 'Show what I can do'}
        </button>
        {showPermissions && (
          <div className="settingsapp__perms">
            {access.isPending && <p className="muted">Loading…</p>}
            {access.isError && (
              <p className="errornote" role="alert">
                <span aria-hidden="true">⚠ </span>
                Could not load the list.
              </p>
            )}
            {access.data && <AccessLines lines={access.data.lines} />}
          </div>
        )}
      </div>
    </section>
  );
}

/** The line along the bottom: which page, whether it saved, what is changed. */
/** Pages of personal settings, which save by themselves. The others have their own Save. */
const PERSONAL_PAGES: SectionId[] = ['display', 'accessibility', 'windows'];

function SettingsStatus({ place, personal }: { place: string; personal: boolean }) {
  const { prefs, saving, error, savedAt, retry } = usePreferences();
  const changed = changedKeys(prefs).length;

  return (
    <StatusBar>
      <span>{place}</span>
      {saving ? (
        <span>Saving…</span>
      ) : error ? (
        <span className="statusbar__error" role="alert">
          <span aria-hidden="true">⚠ </span>
          {error}{' '}
          <button type="button" className="linkbutton" onClick={retry}>
            Try again
          </button>
        </span>
      ) : personal ? (
        <span>{savedAt ? `Saved to your account at ${formatTime(savedAt)}` : 'Changes save by themselves'}</span>
      ) : null}
      <span>{changed === 0 ? 'All settings at default' : `${changed} changed from default`}</span>
    </StatusBar>
  );
}
